import { makeAutoObservable, observable } from 'mobx';

export type SortKey = 'name' | 'length' | 'clips' | 'lastUpdate';
export type SortDir = 'asc' | 'desc';

/**
 * Ordering of the root-level "Recent Stories" list. The two modes are peer
 * sorts over the same set — both show every story — so neither is a narrowing
 * of the other (which is why the toggle renders neutral, not accented).
 */
export type RecentSortMode = 'activity' | 'uploaded';

// Folder-tree panel width: the open default is also the minimum (can't shrink
// below it); the user can drag the right edge out to the max.
export const TREE_MIN_WIDTH = 240;
export const TREE_MAX_WIDTH = 480;

// The tree open/closed + width preference is mirrored to localStorage so it
// survives a full page reload (in-session it lives in this store).
const TREE_VISIBLE_KEY = 'storyManager.folderTree.visible';
const TREE_WIDTH_KEY = 'storyManager.folderTree.width';

function readTreeVisible(): boolean {
  try {
    return localStorage.getItem(TREE_VISIBLE_KEY) === 'true';
  } catch {
    return false;
  }
}

function readTreeWidth(): number {
  try {
    const raw = localStorage.getItem(TREE_WIDTH_KEY);
    const parsed = raw === null ? NaN : Number(raw);
    if (Number.isNaN(parsed)) return TREE_MIN_WIDTH;
    return Math.min(TREE_MAX_WIDTH, Math.max(TREE_MIN_WIDTH, parsed));
  } catch {
    return TREE_MIN_WIDTH;
  }
}

/**
 * Ephemeral Story Manager view state — the UI state that should survive
 * navigating away and back within a session (Story Manager unmounts on
 * navigation, so this outlives the component). Folder *data* and the current
 * location live in `FolderStructureStore`; the story list lives in
 * `DashboardStore`. This holds only view chrome: selection, expansion, sort,
 * the folder-tree panel, and scroll position.
 */
export class StoryManagerViewStore {
  // Single-item row highlight — one folder, story, OR clip at a time.
  selectedFolderId: string | null = null;
  selectedStoryId: string | null = null;
  selectedClipId: string | null = null;

  // Bulk (checkbox) multi-selection that drives the BulkActionBar (move /
  // delete). Distinct from the single-item highlight above. Replaced wholesale
  // (observable.ref) rather than deep-observed.
  selectedStoryIds: Set<string> = new Set();

  // Details slide-over open/closed. Its content follows the highlight.
  detailsOpen = false;

  // Per-story clip-expansion overrides: an explicit user toggle (true =
  // expanded, false = collapsed) wins over the search auto-expand default.
  // Replaced wholesale (observable.ref) rather than deep-observed.
  storyExpansionOverrides: Map<string, boolean> = new Map();

  // Story-list sort.
  storySortKey: SortKey | null = 'lastUpdate';
  storySortDir: 'asc' | 'desc' = 'desc';

  // Root-level "Recent Stories" ordering. Defaults to activity: the list's job
  // is surfacing what's being worked on, not what arrived.
  recentSortMode: RecentSortMode = 'activity';

  // Folder-tree sidebar: which nodes are expanded + the "Home" root node.
  expandedFolderIds: Set<string> = new Set();
  showcaseTreeExpanded = true;

  // Folder-tree panel open/closed + width (persisted to localStorage).
  treeVisible: boolean = readTreeVisible();
  treeWidth: number = readTreeWidth();

  // Scroll position of the story-list container. Non-observable — it is only
  // read imperatively on restore, so it must not trigger re-renders.
  scrollTop = 0;

  // The album/showcase the row-scoped state above belongs to. Guards
  // `resetForShowcase` so returning to the same org preserves state, but
  // switching orgs clears it (mirrors `FolderStructureStore.showcaseId`).
  showcaseId: string | null = null;

  constructor() {
    makeAutoObservable(
      this,
      {
        storyExpansionOverrides: observable.ref,
        expandedFolderIds: observable.ref,
        selectedStoryIds: observable.ref,
        scrollTop: false,
        showcaseId: false,
      },
      { autoBind: true },
    );
  }

  /**
   * Scope the row-level view state to a showcase/org. No-op when the showcase
   * is unchanged (so navigating away and back preserves everything); on an
   * actual org switch it clears selection, expansion, details, and scroll so a
   * previous org's state doesn't leak onto another org's rows. Cross-org UI
   * preferences (tree panel visible/width, sort) are intentionally kept.
   */
  resetForShowcase(showcaseId: string) {
    if (this.showcaseId === showcaseId) return;
    this.showcaseId = showcaseId;
    this.selectedFolderId = null;
    this.selectedStoryId = null;
    this.selectedClipId = null;
    this.detailsOpen = false;
    this.selectedStoryIds = new Set();
    this.storyExpansionOverrides = new Map();
    this.expandedFolderIds = new Set();
    this.showcaseTreeExpanded = true;
    this.scrollTop = 0;
  }

  // ─── Selection ────────────────────────────────────────────────────────────

  selectFolder(id: string | null) {
    this.selectedFolderId = id;
    this.selectedStoryId = null;
    this.selectedClipId = null;
  }

  selectStory(id: string | null) {
    this.selectedStoryId = id;
    this.selectedFolderId = null;
    this.selectedClipId = null;
  }

  selectClip(id: string | null) {
    this.selectedClipId = id;
    this.selectedFolderId = null;
    this.selectedStoryId = null;
  }

  clearRowSelection() {
    this.selectedFolderId = null;
    this.selectedStoryId = null;
    this.selectedClipId = null;
  }

  setDetailsOpen(open: boolean) {
    this.detailsOpen = open;
  }

  // ─── Bulk (checkbox) selection ──────────────────────────────────────────

  toggleStorySelection(storyId: string) {
    const next = new Set(this.selectedStoryIds);
    if (next.has(storyId)) next.delete(storyId);
    else next.add(storyId);
    this.selectedStoryIds = next;
  }

  clearStorySelection() {
    if (this.selectedStoryIds.size === 0) return;
    this.selectedStoryIds = new Set();
  }

  // ─── Story rows ─────────────────────────────────────────────────────────

  setStoryExpanded(storyId: string, expanded: boolean) {
    const next = new Map(this.storyExpansionOverrides);
    next.set(storyId, expanded);
    this.storyExpansionOverrides = next;
  }

  setRecentSortMode(mode: RecentSortMode) {
    this.recentSortMode = mode;
  }

  toggleSort(key: SortKey) {
    if (this.storySortKey === key) {
      this.storySortDir = this.storySortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.storySortKey = key;
      this.storySortDir = 'asc';
    }
  }

  // ─── Folder tree ──────────────────────────────────────────────────────────

  toggleFolderExpanded(id: string) {
    const next = new Set(this.expandedFolderIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.expandedFolderIds = next;
  }

  /** Additively expand nodes (e.g. ancestors of the current folder). */
  addExpandedFolders(ids: string[]) {
    if (ids.length === 0) return;
    const next = new Set(this.expandedFolderIds);
    ids.forEach(id => next.add(id));
    this.expandedFolderIds = next;
  }

  setShowcaseTreeExpanded(expanded: boolean) {
    this.showcaseTreeExpanded = expanded;
  }

  // ─── Tree panel (persisted) ─────────────────────────────────────────────

  toggleTree() {
    this.treeVisible = !this.treeVisible;
    try {
      localStorage.setItem(TREE_VISIBLE_KEY, String(this.treeVisible));
    } catch {
      // Best-effort persistence.
    }
  }

  /** Live width update while dragging (clamped). Not yet persisted. */
  setTreeWidth(width: number) {
    this.treeWidth = Math.min(TREE_MAX_WIDTH, Math.max(TREE_MIN_WIDTH, width));
  }

  /** Persist the width once the drag ends. */
  commitTreeWidth() {
    try {
      localStorage.setItem(TREE_WIDTH_KEY, String(this.treeWidth));
    } catch {
      // Best-effort persistence.
    }
  }

  // ─── Scroll ───────────────────────────────────────────────────────────────

  setScrollTop(top: number) {
    this.scrollTop = top;
  }
}
